import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PrinterIcon, MountainsIcon } from "@phosphor-icons/react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function GstReportPrint() {
  const [sp] = useSearchParams();
  const [platform, setPlatform] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = { start: sp.get("start"), end: sp.get("end") };
    const pid = sp.get("property_id");
    if (pid) params.property_id = pid;
    api.get("/reports/gst", { params }).then((r) => setData(r.data));
    api.get("/settings/platform").then((r) => setPlatform(r.data));
  }, [sp]);

  if (!data) return <div className="min-h-screen flex items-center justify-center label-overline">Loading…</div>;

  return (
    <div className="min-h-screen bg-muted/40 print:bg-white">
      <div className="print:hidden sticky top-0 z-10 bg-card border-b border-border">
        <div className="max-w-5xl mx-auto flex items-center justify-between p-4">
          <p className="label-overline">GST Report · {sp.get("start")} to {sp.get("end")}</p>
          <Button onClick={() => window.print()}><PrinterIcon size={14} className="mr-1" />Print / Save PDF</Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto my-8 bg-white shadow-sm border border-border p-10 print:my-0 print:shadow-none print:border-0">
        <div className="flex items-start justify-between pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <MountainsIcon size={30} weight="duotone" className="text-primary" />
            <div>
              <h1 className="font-display text-2xl">{platform?.group_name || "Hotel Group"}</h1>
              <p className="text-xs text-muted-foreground">{platform?.address}</p>
              {platform?.gst_number && <p className="text-xs text-muted-foreground">GSTIN: {platform.gst_number}</p>}
            </div>
          </div>
          <div className="text-right">
            <p className="label-overline">GST Report</p>
            <p className="font-mono-plex text-sm mt-1">{sp.get("start")} → {sp.get("end")}</p>
          </div>
        </div>

        <div className="grid grid-cols-5 gap-3 my-6">
          {[
            ["Invoices", data.summary.total_invoices],
            ["Taxable", inr(data.summary.taxable_total)],
            ["CGST", inr(data.summary.cgst_total)],
            ["SGST", inr(data.summary.sgst_total)],
            ["Gross", inr(data.summary.gross_total)],
          ].map(([k, v]) => (
            <div key={k} className="border border-border rounded p-3">
              <p className="label-overline">{k}</p>
              <p className="font-mono-plex text-base mt-1">{v}</p>
            </div>
          ))}
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-border text-left">
              <th className="py-2">Invoice #</th><th className="py-2">Date</th><th className="py-2">Property</th><th className="py-2">Guest</th>
              <th className="py-2 text-right">Taxable</th><th className="py-2 text-right">CGST</th><th className="py-2 text-right">SGST</th><th className="py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.invoices.map((inv) => (
              <tr key={inv.id} className="border-b border-border/60">
                <td className="py-2 font-mono-plex text-xs">{inv.number}</td>
                <td className="py-2 text-xs">{inv.created_at?.slice(0, 10)}</td>
                <td className="py-2 text-xs">{inv.property_name}</td>
                <td className="py-2 text-xs">{inv.guest_name}</td>
                <td className="py-2 text-right font-mono-plex text-xs">{inr(inv.taxable_amount)}</td>
                <td className="py-2 text-right font-mono-plex text-xs">{inr(inv.cgst)}</td>
                <td className="py-2 text-right font-mono-plex text-xs">{inr(inv.sgst)}</td>
                <td className="py-2 text-right font-mono-plex text-xs font-medium">{inr(inv.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="mt-8 text-xs text-muted-foreground">
          Generated on {new Date().toLocaleString("en-IN")}. Prepared by {platform?.group_name} for CA/GSTR filing purposes.
        </p>
      </div>

      <style>{`@media print { @page { size: A4 landscape; margin: 10mm; } body { background: white !important; } }`}</style>
    </div>
  );
}
